'use client'

import { useState, type ComponentProps } from 'react'
import Link from 'next/link'
import {
  Lock,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Fingerprint,
  Loader2,
} from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { Button } from '@/components/ui/button'
import { openCheckout } from '@/lib/paddle/checkout'
import BrandDisambiguationResults from './components/BrandDisambiguationResults'
import { useToolRunOrCheckout } from '@/hooks/useToolRunOrCheckout'

// Clearly-labeled illustrative example. Not real data about any real brand.
const EXAMPLE = {
  brand: 'Northwind Labs',
  score: 70,
  grade: 'B',
  bySeverity: { high: 1, medium: 1, low: 0 },
  flaggedEngines: [
    { provider: 'ChatGPT', severity: 'high' as const },
    { provider: 'Gemini', severity: 'medium' as const },
  ],
  cleanEngines: ['Claude', 'Perplexity'],
}

const SEVERITY: Record<
  'high' | 'medium' | 'low',
  { label: string; color: string }
> = {
  high: { label: 'High', color: '#F90B8A' },
  medium: { label: 'Medium', color: '#f97316' },
  low: { label: 'Low', color: '#FDC304' },
}

function ExampleReport() {
  return (
    <div className='bg-muted/50 rounded-2xl border border-line-strong p-6 relative'>
      <div className='flex items-center justify-between mb-5'>
        <span className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground leading-none'>
          Example output (not your brand)
        </span>
        <span className='text-[11px] font-medium text-muted-foreground leading-none'>
          {EXAMPLE.brand}
        </span>
      </div>

      <div className='flex flex-col items-center gap-3 mb-5'>
        <p className='text-5xl font-black leading-none text-primary'>
          {EXAMPLE.score}
        </p>
        <span className='inline-block bg-primary/10 px-3 py-1 text-sm font-bold text-primary'>
          Grade {EXAMPLE.grade}
        </span>
      </div>

      <div className='grid grid-cols-3 gap-3 mb-5'>
        {(['high', 'medium', 'low'] as const).map(sev => (
          <div
            key={sev}
            className='bg-background border border-line-strong p-4 text-center'
          >
            <p
              className='text-2xl font-black leading-none mb-1'
              style={{ color: SEVERITY[sev].color }}
            >
              {EXAMPLE.bySeverity[sev]}
            </p>
            <p className='text-[11px] uppercase tracking-wide text-muted-foreground leading-none'>
              {SEVERITY[sev].label}
            </p>
          </div>
        ))}
      </div>

      <div className='space-y-2'>
        {EXAMPLE.flaggedEngines.map(e => (
          <div
            key={e.provider}
            className='flex items-center gap-3 bg-background border border-line-strong p-3'
          >
            <AlertTriangle size={14} className='text-[#F90B8A] shrink-0' />
            <span className='text-sm font-medium text-foreground leading-none w-20 shrink-0'>
              {e.provider}
            </span>
            <span className='text-xs text-muted-foreground leading-none'>
              Confused with
            </span>
            <span className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground select-none'>
              <span className='inline-block h-3 w-28 rounded bg-foreground/15 align-middle' />
              <Lock size={12} />
            </span>
            <span
              className='ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 shrink-0'
              style={{
                backgroundColor: `${SEVERITY[e.severity].color}15`,
                color: SEVERITY[e.severity].color,
              }}
            >
              {SEVERITY[e.severity].label}
            </span>
          </div>
        ))}
        {EXAMPLE.cleanEngines.map(p => (
          <div
            key={p}
            className='flex items-center gap-3 bg-background border border-line-strong p-3'
          >
            <CheckCircle2 size={14} className='text-[#22c55e] shrink-0' />
            <span className='text-sm font-medium text-foreground leading-none w-20 shrink-0'>
              {p}
            </span>
            <span className='text-xs text-muted-foreground leading-none'>
              Identified correctly
            </span>
          </div>
        ))}
      </div>

      <p className='text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5'>
        <Lock size={11} />
        The entity each engine confused you with, plus the fixes, unlock inside
        Radar.
      </p>
    </div>
  )
}

export default function BrandDisambiguationTeaserPage() {
  const [brand, setBrand] = useState('')
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Frozen at submit so editing the form after the wall appears cannot change
  // what the $5 checkout pays to run.
  const [submitted, setSubmitted] = useState({ brand: '', url: '' })
  // Owner/paid → full result inline (free); everyone else → 402 → the $5 wall.
  const {
    run,
    loading,
    error: runError,
    result,
    walled,
  } = useToolRunOrCheckout<
    ComponentProps<typeof BrandDisambiguationResults>['result']
  >('/api/tools/brand-disambiguation')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // The check needs both the brand and the URL, so require both before the
    // buyer pays — a paid run that 400s on missing input would be a bad charge.
    if (!url.trim() || !brand.trim()) {
      setError('Enter both your brand name and website URL.')
      return
    }
    setError(null)
    setSubmitted({ brand: brand.trim(), url: url.trim() })
    void run({ url: url.trim(), brand: brand.trim() })
    setTimeout(() => {
      document
        .getElementById('disambiguation-output')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const label = brand.trim() || url.trim() || 'your brand'

  return (
    <div className='animate-fade-in'>
      <div className='container mx-auto px-4 py-16 pb-24'>
        {/* Hero */}
        <LenisFadeIn>
          <div className='text-center mb-10'>
            <h1 className='mb-4 font-heading max-w-4xl mx-auto'>
              Do AI Engines Confuse Your Brand With Someone Else?
            </h1>
            <p className='lead max-w-2xl mx-auto mb-6'>
              When a brand shares its name with another company, person, or
              product, AI engines can describe the wrong one. Brand
              Disambiguation checks whether ChatGPT, Claude, Perplexity, and
              Gemini link your name to the right entity.
            </p>

            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mb-8'>
              This is Named Entity Disambiguation (NED). Radar reads your real
              identity from your site and llms.txt, asks each AI engine an open
              question about your brand, and judges whether the answer describes
              you or a different thing that happens to share your name. An
              entity collision means buyers asking an AI about you get details
              about an unrelated company, quietly redirecting trust and traffic
              away from you.
            </p>

            {/* Intent form */}
            <form onSubmit={handleSubmit} className='w-full max-w-2xl mx-auto'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3'>
                <input
                  type='text'
                  value={brand}
                  onChange={e => setBrand(e.target.value)}
                  placeholder='Brand'
                  className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
                />
                <input
                  type='text'
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder='Website URL (e.g. yourbrand.com)'
                  className='w-full h-12 px-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
                />
              </div>
              <Button
                type='submit'
                variant='default'
                size='lg'
                loading={loading}
                disabled={loading}
                className='h-12 px-8 w-full sm:w-auto sm:mx-auto'
              >
                <Fingerprint size={18} />
                Check Disambiguation
              </Button>
            </form>
            <p className='text-xs text-muted-foreground mt-3'>
              Pay a one-time $5 to run this check. Your report opens right here.
            </p>
            {error && (
              <p className='text-sm text-[#F90B8A] mt-4 bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {error}
              </p>
            )}
          </div>
        </LenisFadeIn>

        {/* Output: loading / error / result (owner inline) / $5 wall */}
        {(loading || runError || result || walled) && (
          <div id='disambiguation-output' className='max-w-3xl mx-auto mb-12'>
            {loading && (
              <div className='flex items-center justify-center gap-3 rounded-2xl border border-line-strong bg-muted/50 p-8 text-sm text-muted-foreground'>
                <Loader2 size={18} className='animate-spin' />
                Checking disambiguation for {label}…
              </div>
            )}

            {runError && !loading && (
              <p className='text-sm text-[#F90B8A] bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {runError}
              </p>
            )}

            {result && !loading && (
              <BrandDisambiguationResults result={result} />
            )}

            {walled && !loading && (
              <div className='max-w-2xl mx-auto'>
                <div className='rounded-2xl border border-[#0ea5e9]/30 bg-[#0ea5e9]/5 p-8 text-center'>
                  <div className='flex justify-center mb-3'>
                    <Lock size={22} className='text-[#0ea5e9]' />
                  </div>
                  <p className='text-base font-semibold text-foreground mb-2'>
                    Run the disambiguation check for {label}
                  </p>
                  <p className='text-sm text-muted-foreground mb-6 max-w-md mx-auto'>
                    Pay a one-time $5 to run the full check on{' '}
                    <span className='font-medium text-foreground'>{label}</span>
                    : all 4 AI engines, the exact entity each one confused you
                    with, and the fixes to reclaim your identity. Your report
                    opens right here.
                  </p>
                  <div className='flex flex-col sm:flex-row gap-3 justify-center'>
                    <Button
                      type='button'
                      variant='default'
                      size='lg'
                      onClick={() =>
                        openCheckout('pack-single', 'tool_teaser', {
                          source: 'tool_run',
                          toolSlug: 'brand-disambiguation',
                          url: submitted.url,
                          brand: submitted.brand,
                        })
                      }
                      className='h-12 px-7'
                    >
                      Run for $5
                      <ArrowRight size={16} />
                    </Button>
                    <Link
                      href='/platform#pricing'
                      className='inline-flex items-center justify-center gap-2 h-12 px-7 text-sm font-semibold text-foreground border border-border transition-colors hover:bg-muted'
                    >
                      See pricing
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Example output */}
        <LenisFadeIn>
          <div className='max-w-2xl mx-auto mb-12'>
            <ExampleReport />
          </div>
        </LenisFadeIn>

        {/* What we check */}
        <LenisFadeIn>
          <div className='max-w-3xl mx-auto mb-12'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
                What the full check does
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                {[
                  {
                    title: 'Your verified identity',
                    desc: 'Reads your homepage, about pages, and llms.txt to extract who you actually are: category, products, and leadership.',
                    color: '#008dcc',
                  },
                  {
                    title: '4 AI engines',
                    desc: 'ChatGPT, Claude, Perplexity, and Gemini each get an open identity question about your brand.',
                    color: '#8b5cf6',
                  },
                  {
                    title: 'Entity collisions',
                    desc: 'A judge compares each answer to your real identity and names the different, same-named entity it described instead.',
                    color: '#F90B8A',
                  },
                  {
                    title: 'Fixes that reclaim you',
                    desc: 'Severity scoring plus the schema and llms.txt moves that make the correct entity dominate.',
                    color: '#FDC304',
                  },
                ].map(item => (
                  <FeatureCard key={item.title} {...item} />
                ))}
              </div>
            </div>
          </div>
        </LenisFadeIn>

        {/* Why it matters */}
        <LenisFadeIn>
          <div className='max-w-4xl mx-auto'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-2 text-center'>
                Why entity disambiguation matters
              </h2>
              <p className='text-sm text-muted-foreground text-center mb-8 max-w-2xl mx-auto'>
                Citation tracking asks whether AI mentions you. Disambiguation
                asks whether the AI mentioning you is describing the right you.
                A strong citation score still loses if the engine attaches your
                name to someone else.
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                {[
                  {
                    title: 'How to Build a Brand That AI Search Engines Cite',
                    href: '/blogs/build-brand-ai-search-engines-cite',
                    tag: 'Brand',
                    color: '#008dcc',
                  },
                  {
                    title:
                      'The GEO Playbook: How to Get Cited by ChatGPT, Perplexity, and Claude',
                    href: '/blogs/geo-playbook-get-cited-chatgpt-perplexity-claude',
                    tag: 'GEO',
                    color: '#F90B8A',
                  },
                  {
                    title:
                      'SEO vs AEO vs GEO: From Ranking in Search to Becoming the Recommended Brand',
                    href: '/blogs/seo-vs-geo-vs-aeo-guide-2026',
                    tag: 'Strategy',
                    color: '#f97316',
                  },
                  {
                    title: 'We Optimized for AI Search. Here’s What Changed.',
                    href: '/blogs/optimized-ai-search-pixelmojo-results',
                    tag: 'Results',
                    color: '#22c55e',
                  },
                ].map(post => (
                  <a
                    key={post.href}
                    href={post.href}
                    className='flex items-start gap-2.5 rounded-lg p-2.5 transition-colors hover:bg-muted'
                  >
                    <span
                      className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 shrink-0 mt-0.5'
                      style={{
                        backgroundColor: `${post.color}15`,
                        color: post.color,
                      }}
                    >
                      {post.tag}
                    </span>
                    <span className='text-xs text-foreground/80 leading-relaxed'>
                      {post.title}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
